// src/App.js

import React, { useState, useRef, useEffect, useContext, useCallback } from "react";
import {
  Box,
  CssBaseline,
  useMediaQuery,
  CircularProgress,
  useTheme,
  Snackbar,
  Alert
} from "@mui/material";
import { ThemeProvider } from '@mui/material/styles';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import theme, { tokens } from './theme/index'; // Importing theme and tokens properly
import { PaginationProvider, PaginationContext } from './context/PaginationContext';
import { FormProvider, FormContext } from "./context/FormContext";
import { TypographyProvider } from './context/TypographyContext';
import { LayoutProvider, useLayout } from './context/LayoutContext';
import { DesignProvider, useDesign } from './context/DesignContext';
import { TemplateProvider } from './context/TemplateContext';
import { StyleProvider, useStyle } from './context/StyleContext';
import { BackgroundProvider, useBackground } from './context/BackgroundContext';
import { SpecialEffectProvider, useSpecialEffect } from './context/SpecialEffectContext';
import InputSection, { allSections } from "./components/InputSection/InputSection";
import LivePreview from "./components/PreviewSection/LivePreview";
import Header from './components/bars/Header';
import MobileNavBar from './components/bars/MobileNavBar';
import SettingsPanel from './components/SettingsSection/SettingsPanel';
import WelcomeScreen from './components/WelcomeScreen/WelcomeScreen';
import { BarStyleProvider, useBarStyle } from './context/BarStyleContext';
import LandingPage from './components/LandingPage/LandingPage';
import { ImpressumPage, AGBPage, DatenschutzPage } from './components/LandingPage/LegalPages';
import { LanguageProvider } from './localization';

// Import services needed for handlers
import { updateAppContext, SUPPORTED_EXTENSIONS, SUPPORTED_MIME_TYPES, parseDocx, parsePdf } from './services/ImportService';
import { enhancedImportJSON } from './services/ImportExportUtils';
import { parseLinkedInZip } from './services/linkedin/LinkedInParser';

import { useTypography } from "./context/TypographyContext";
import { clearAllData } from "./services/DataService";
import { loadAndApplyPersonaById } from './services/PersonaService';

  // Import the new ExportProvider
import { ExportProvider, useExport } from './context/ExportContext';

// Main content component with lifted state management
function AppContent() {
  // Base state
  const [selectedSections, setSelectedSections] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showSettingsInEditor, setShowSettingsInEditor] = useState(false);
  const [loading] = useState(false);
  const previewRef = useRef(null);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const [wasManuallyToggled, setWasManuallyToggled] = useState(false);

  // --- NEW: State for welcome screen ---
  const [hasStarted, setHasStarted] = useState(false);
  // --- END NEW State ---

  // Theme and responsive utilities
  const appTheme = useTheme();
  const isMobile = useMediaQuery(appTheme.breakpoints.down('lg'));

  // --- NEW: Get URL params to check for template selection ---
  const location = useLocation();
  // --- END NEW ---

  // --- NEW: State for pending upload trigger ---
  const [pendingUploadTrigger, setPendingUploadTrigger] = useState(null);
  // --- END NEW State ---

  // --- Lifted State & Refs ---
  const { formData, updateFormData } = useContext(FormContext);
  const { selectedLayout, setSelectedLayout } = useLayout();
  const { setSelectedDesign, setFullCustomColorScheme } = useDesign();
  const { selectedStyleId, setSelectedStyleId } = useStyle();
  const { selectedBackgroundId, setSelectedBackgroundId } = useBackground();
  const { selectedSpecialEffectId, setSelectedSpecialEffectId } = useSpecialEffect();
  const { setSelectedBarStyleId } = useBarStyle();
  const { updateTypographySettings, typographySettings } = useTypography();
  const { setPages, setCurrentPage } = useContext(PaginationContext);
  const { initiatePdfExport } = useExport();

  const fileInputRef = useRef(null);
  const linkedInInputRef = useRef(null);
  const docxInputRef = useRef(null);
  const pdfInputRef = useRef(null);

  const [isImportingLinkedIn, setIsImportingLinkedIn] = useState(false);
  const [isImportingDocx, setIsImportingDocx] = useState(false);
  const [isImportingPdf, setIsImportingPdf] = useState(false);
  const isImportingAny = isImportingLinkedIn || isImportingDocx || isImportingPdf;

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  // --- End Lifted State & Refs ---



  // --- Memoized Handlers --- 
  const handleStartManually = useCallback(() => {
    setHasStarted(true);
  }, []);

  const handleBackToWelcome = useCallback(() => {
    setHasStarted(false);
  }, []);

  const handleSnackbarClose = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  }, []);

  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  // Memoize the generic import processor callback
  const processImport = useCallback(async (event, parser, fileExtension, importType, setLoadingState) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(fileExtension)) {
      showSnackbar(`Invalid file type. Please upload a ${fileExtension} file.`, 'error');
      event.target.value = null;
      return;
    }
    if (importType === 'PDF' && file.type !== SUPPORTED_MIME_TYPES.PDF) {
      showSnackbar(`Invalid file type. Please upload a ${fileExtension} file.`, 'error');
      event.target.value = null;
      return;
    }

    setLoadingState(true);
    try {
      const parsedResult = await parser(file);
      console.log(`${importType} Import Result:`, parsedResult);
      const contextSetters = { updateFormData, setSelectedSections };
      const importConfirmedAndApplied = updateAppContext(parsedResult, contextSetters);

      if (importConfirmedAndApplied) {
        showSnackbar(`${importType} data imported successfully!`, 'success');
        setHasStarted(true); // Mark as started after successful import
      }
    } catch (error) {
      console.error(`${importType} import failed:`, error);
      showSnackbar(`Failed to import ${importType} data: ${error.message}`, 'error');
    } finally {
      setLoadingState(false);
      event.target.value = null;
    }
  }, [updateFormData, setSelectedSections, showSnackbar]); 

  const handleJsonImport = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;
    enhancedImportJSON(
      file,
      setSelectedLayout,
      setFullCustomColorScheme,
      setSelectedDesign,
      setSelectedStyleId, // Use correct setter
      setSelectedBackgroundId, // Use correct setter
      updateTypographySettings,
      setSelectedSpecialEffectId,
      setSelectedBarStyleId,
      updateFormData,
      setSelectedSections,
      allSections,
      {
        showSuccessMessage: true,
        onComplete: () => {
          if (event.target) event.target.value = null;
          setHasStarted(true); // Mark as started after successful import
        }
      }
    ).catch(err => {
      showSnackbar(`Failed to import JSON: ${err.message}`, 'error');
    });
  }, [setSelectedLayout, setFullCustomColorScheme, setSelectedDesign, setSelectedStyleId, setSelectedBackgroundId, updateTypographySettings, setSelectedSpecialEffectId, setSelectedBarStyleId, updateFormData, setSelectedSections, showSnackbar]);

  const handleLinkedInImport = useCallback((event) => processImport(event, parseLinkedInZip, SUPPORTED_EXTENSIONS.LINKEDIN_ZIP, 'LinkedIn', setIsImportingLinkedIn), [processImport]);
  const handleDocxImport = useCallback((event) => processImport(event, parseDocx, SUPPORTED_EXTENSIONS.DOCX, 'DocX', setIsImportingDocx), [processImport]);
  const handlePdfImport = useCallback((event) => processImport(event, parsePdf, SUPPORTED_EXTENSIONS.PDF, 'PDF', setIsImportingPdf), [processImport]);

  // Memoize panel toggle callback
  const togglePanelCollapse = useCallback(() => {
    setIsPanelCollapsed(prev => !prev);
    setWasManuallyToggled(true);
    console.log("Manual toggle triggered");
  }, []);

  // Memoize editor view toggle callback
  const toggleEditorView = useCallback(() => {
    console.log("Toggling left column view (Input/Settings)");
    setShowSettingsInEditor(prev => !prev);
  }, []);

  const triggerJsonUpload = useCallback(() => {
    setHasStarted(true); // Mark as started when user initiates upload
    fileInputRef.current?.click();
  }, []);
  
  const triggerLinkedInUpload = useCallback(() => {
    setHasStarted(true); // Mark as started when user initiates upload
    linkedInInputRef.current?.click();
  }, []);
  
  const triggerDocxUpload = useCallback(() => {
    setHasStarted(true); // Mark as started when user initiates upload
    docxInputRef.current?.click();
  }, []);
  
  const triggerPdfUpload = useCallback(() => {
    setHasStarted(true); // Mark as started when user initiates upload
    pdfInputRef.current?.click();
  }, []);



  // --- Memoize ClearAllData handler ---
  const handleClearAll = useCallback(() => {
    clearAllData(updateFormData, setSelectedSections, {
      showConfirmation: true, // Assuming you want confirmation
      onComplete: () => {
        // Reset other relevant states if needed
        setPages([]);
        setCurrentPage(0);
        console.log("All data cleared.");
      }
    });
  }, [updateFormData, setSelectedSections, setPages, setCurrentPage]); // Add dependencies

  // Auto-collapse Logic
  const minLeftWidth = 500;
  const minMiddleWidth = 500;
  const minRightWidthExpanded = 500;
  const collapsedRightWidth = 40;
  const autoCollapseWidthThreshold = minLeftWidth + minMiddleWidth + minRightWidthExpanded + 16;
  const shouldAutoCollapse = useMediaQuery(`(max-width: ${autoCollapseWidthThreshold}px)`);
  const isNarrowDesktop = !isMobile && shouldAutoCollapse;

  useEffect(() => {
    // Only apply auto-collapse on non-mobile screens where the panel is visible
    if (!isMobile) {
      if (shouldAutoCollapse && !isPanelCollapsed && !wasManuallyToggled) {
        // Auto-collapse if width is too small and user hasn't manually intervened
        console.log("Auto-collapsing settings panel");
        setIsPanelCollapsed(true);
      } else if (!shouldAutoCollapse && isPanelCollapsed && !wasManuallyToggled) {
        // Auto-expand if there's enough space and user hasn't manually collapsed
        console.log("Auto-expanding settings panel");
        setIsPanelCollapsed(false);
      }
    } else {
      // Reset manual toggle state if screen becomes mobile
      setWasManuallyToggled(false);
       // Ensure panel is not collapsed if switching back from mobile to desktop with enough space
      if(!shouldAutoCollapse) {
          setIsPanelCollapsed(false);
      }
    }

    // Reset left column view if resizing out of narrow desktop mode
    if (!isMobile && !shouldAutoCollapse && showSettingsInEditor) {
        console.log("Resized out of narrow desktop, showing Input Section");
        setShowSettingsInEditor(false);
    }
    // Reset wasManuallyToggled if becoming mobile
    if (isMobile) {
        setWasManuallyToggled(false);
    }

  }, [shouldAutoCollapse, isMobile, isPanelCollapsed, wasManuallyToggled, showSettingsInEditor]);

  // --- Callbacks for Header/NavBar (memoized) --- 
  const openTypographySettings = useCallback(() => {
    console.log("Trigger Typography Settings");
    // Implement actual modal opening logic here if needed later
  }, []);

  const openDesignLayoutPicker = useCallback(() => {
    console.log("Trigger Design/Layout Picker");
    // Implement actual modal opening logic here if needed later
  }, []);

  // Calculate widths based on panel state
  const leftColumnWidth = isMobile ? '100%' : '30%';
  // Middle column takes remaining space when right panel collapses fully
  const middleColumnWidth = isMobile
    ? '100%'
    : isNarrowDesktop // If right panel is hidden due to width
      ? '70%'           // Left is 30%, Middle takes 70%
      : (isPanelCollapsed ? `calc(70% - ${collapsedRightWidth}px - 1px)` : '40%'); // Standard collapse logic
  // Right column width changes on collapse
  const rightColumnWidth = isPanelCollapsed ? `${collapsedRightWidth}px` : '30%';

  // --- MOVED: useEffect to handle pending upload trigger (BEFORE conditional return) --- 
  useEffect(() => {
    if (pendingUploadTrigger) {
      console.log(`Effect triggered for upload type: ${pendingUploadTrigger}`);
      switch (pendingUploadTrigger) {
        case 'json':
          triggerJsonUpload();
          break;
        case 'linkedin':
          triggerLinkedInUpload();
          break;
        case 'docx':
          triggerDocxUpload();
          break;
        case 'pdf':
          triggerPdfUpload();
          break;
        default:
          console.warn(`Unknown upload type triggered: ${pendingUploadTrigger}`);
      }
      // Reset the trigger after attempting the upload
      setPendingUploadTrigger(null);
    }
  }, [pendingUploadTrigger, triggerJsonUpload, triggerLinkedInUpload, triggerDocxUpload, triggerPdfUpload]); // Dependencies ensure effect runs when trigger changes
  // --- END MOVED useEffect ---

  // --- NEW: Effect to check for template and persona parameters and auto-start ---
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const templateParam = urlParams.get('template');
    const personaParam = urlParams.get('persona');
    
    if (templateParam) {
      // User came from landing page with a template selected
      console.log('Template parameter detected:', templateParam);
      setHasStarted(true);
    }

    if (personaParam) {
      // User came from landing page with a persona selected
      console.log('Persona parameter detected:', personaParam);
      setHasStarted(true); // Start the app when persona is detected
      
      // Prepare context setters for PersonaService
      const contextSetters = {
        updateFormData,
        setSelectedSections,
        allSections,
        _isPersonaLoad: true // Flag to skip confirmation dialog
      };
      
      // Use PersonaService to load and apply persona data
      loadAndApplyPersonaById(personaParam, contextSetters)
        .then(success => {
          if (success) {
            // Extract profession name for success message
            const professionName = personaParam
              .split('-')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
            showSnackbar(`CV pre-populated with ${professionName} template!`, 'success');
          } else {
            showSnackbar('Failed to load profession template', 'error');
          }
        })
        .catch(error => {
          console.error('Error loading persona data:', error);
          showSnackbar('Failed to load profession template', 'error');
        });
    }
  }, [location.search, updateFormData, setSelectedSections, showSnackbar]);

  // --- NEW: Separate effect for payment success handling ---
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const paymentParam = urlParams.get('payment');

    if (paymentParam === 'success') {
      // User returned from successful payment on mobile
      console.log('Payment success parameter detected - triggering PDF export');
      setHasStarted(true);
      
      // Clean up URL parameter
      const newUrlParams = new URLSearchParams(location.search);
      newUrlParams.delete('payment');
      const newSearch = newUrlParams.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
      window.history.replaceState({}, document.title, newUrl);
      
      // Trigger PDF export after a short delay to ensure app is ready
      setTimeout(async () => {
        try {
          console.log('Triggering PDF export via ExportContext');
          await initiatePdfExport();
          showSnackbar('Payment successful! Your PDF download started.', 'success');
        } catch (error) {
          console.error('Auto PDF export failed:', error);
          showSnackbar('Payment successful! Please click the PDF export button to download.', 'warning');
        }
      }, 1500);
    }
  }, [location.search, initiatePdfExport, showSnackbar]);
  // --- END NEW Effect ---

  useEffect(() => {
    // Route-based logic: Only handle root path redirect, let /app show welcome screen by default
    // The welcome screen will be shown unless user has already started or has URL parameters
    if (location.pathname === '/') {
      setHasStarted(false);
    }
    // Remove auto-start for /app path - let welcome screen show by default
    // Users will start via welcome screen actions or URL parameters (template/persona)
  }, [location.pathname]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      {/* Header with app title and primary actions */}
      <Header 
        isMobile={isMobile}
        selectedSections={selectedSections}
        setSelectedSections={setSelectedSections}
        previewRef={previewRef}
        setShowPreview={setShowPreview}
        openTypographySettings={openTypographySettings}
        openDesignLayoutPicker={openDesignLayoutPicker}
        isNarrowDesktop={isNarrowDesktop}
        showSettingsInEditor={showSettingsInEditor}
        toggleEditorView={toggleEditorView}
        formData={formData}
        selectedLayout={selectedLayout}
        selectedStyle={selectedStyleId}
        selectedBackground={selectedBackgroundId}
        typographySettings={typographySettings}
      />

      {/* Main content area - Three columns on desktop, stacked on mobile */}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          overflow: 'hidden',
          width: '100%', // Ensure main content area takes full width
        }}
      >
        {/* Left column - Input section or Welcome screen */}
        <Box 
          sx={{ 
            width: leftColumnWidth,
            minWidth: isMobile ? 'unset' : `${minLeftWidth}px`,
            flexShrink: 0, // Prevent shrinking below minWidth
            display: !isMobile || (isMobile && !showPreview) ? 'flex' : 'none',
            flexDirection: 'column', // Ensure content flows top-down
            height: isMobile ? (hasStarted ? 'calc(100vh - 56px - 56px)' : 'calc(100vh - 56px)') : '100%',
            overflow: 'auto',
            borderRight: `1px solid ${tokens.colors.borders.default}`,
            transition: 'width 0.3s ease',
            backgroundColor: tokens.colors.background.default, // Add background color
          }}
        >
          <Box sx={{ padding: 2, flexGrow: 1 }}> {/* Allow content padding to grow */}
            {/* --- REFINED Conditional Content --- */}
            {!hasStarted ? (
              // Show Welcome Screen when user hasn't started yet
              <WelcomeScreen
                onStartManual={handleStartManually}
                onUploadJson={triggerJsonUpload}
                onUploadLinkedIn={triggerLinkedInUpload}
                onUploadDocx={triggerDocxUpload}
                onUploadPdf={triggerPdfUpload}
                isImporting={isImportingAny}
              />
            ) : (isNarrowDesktop && showSettingsInEditor) || (isMobile && showSettingsInEditor && !showPreview) ? (
                // Case 1: Narrow Desktop showing Settings OR Mobile showing Settings
               <SettingsPanel 
                   isCollapsed={false} 
                   toggleCollapse={() => {}} 
                   isStandaloneInLeftColumn={true}
               />
            ) : (
                // Case 2: Full Desktop OR Narrow Desktop/Mobile showing Inputs
                <InputSection
                   selectedSections={selectedSections}
                   setSelectedSections={setSelectedSections}
                   onBackToWelcome={handleBackToWelcome}
               />
            )}
            {/* --- END REFINED Conditional Content --- */}
          </Box>
        </Box>

        {/* Middle column - Preview */}
        <Box 
          sx={{ 
            width: middleColumnWidth, // Use updated calculation
            minWidth: isMobile ? 'unset' : `${minMiddleWidth}px`,
            flexGrow: 1, // Allow preview to grow if space allows
            flexShrink: 0, // Prevent shrinking below minWidth
            display: !isMobile || (isMobile && showPreview) ? 'flex' : 'none',
            flexDirection: 'column', // Ensure content flows top-down
            height: isMobile ? (hasStarted ? 'calc(100vh - 56px - 56px)' : 'calc(100vh - 56px)') : '100%',
            overflow: 'hidden', // Keep overflow hidden for preview scaling
            borderRight: `1px solid ${tokens.colors.borders.default}`,
            position: 'relative',
            transition: 'width 0.3s ease',
            backgroundColor: tokens.colors.background.alt, // Add contrasting background
          }}
          ref={previewRef}
        >
          {/* Loading spinner */}
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 10,
                backgroundColor: 'rgba(255,255,255,0.7)',
              }}
            >
              <CircularProgress />
            </Box>
          )}
          {/* LivePreview should handle its own scrolling/sizing */}
          <LivePreview 
            selectedSections={selectedSections}
            setSelectedSections={setSelectedSections}
            showSettingsInEditor={showSettingsInEditor}
            setShowSettingsInEditor={setShowSettingsInEditor}
            handleClearAll={handleClearAll}
            selectedSpecialEffectId={selectedSpecialEffectId}
          />
        </Box>

        {/* Right column - Settings Panel (with tabs) - collapsible */}
        {/* Show only if NOT mobile AND there IS enough space */}
        {!isMobile && !isNarrowDesktop && (
          <Box 
            sx={{ 
              width: rightColumnWidth, 
              minWidth: isPanelCollapsed ? `${collapsedRightWidth}px` : `${minRightWidthExpanded}px`,
              flexShrink: 0,
              height: '100%',
              overflow: 'hidden', // Hide overflow when panel is collapsed
              transition: 'width 0.3s ease',
              borderLeft: isPanelCollapsed ? 'none' : `1px solid ${tokens.colors.borders.default}`,
              position: 'relative', // Added for potential absolute positioned children
            }}
          >
            <SettingsPanel 
              isCollapsed={isPanelCollapsed} 
              toggleCollapse={togglePanelCollapse}
              isStandaloneInLeftColumn={false}
            />
          </Box>
        )}
      </Box>



      {/* Mobile Navigation Bar */}
      {isMobile && hasStarted && (
        <MobileNavBar
          showPreview={showPreview}
          setShowPreview={setShowPreview}
          openTypographySettings={openTypographySettings}
          openDesignLayoutPicker={openDesignLayoutPicker}
          previewRef={previewRef}
          setSelectedSections={setSelectedSections}
          selectedSections={selectedSections}
          showSettingsInEditor={showSettingsInEditor}
          toggleEditorView={toggleEditorView}
        />
      )}

      {/* Render Modal and Snackbar globally here */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* --- Hidden File Inputs (kept here for access by handlers) --- */}
      <input
        type="file"
        accept="application/json"
        hidden
        onChange={handleJsonImport}
        ref={fileInputRef}
      />
      <input
        type="file"
        accept={SUPPORTED_EXTENSIONS.LINKEDIN_ZIP}
        hidden
        ref={linkedInInputRef}
        onChange={handleLinkedInImport}
      />
      <input
        type="file"
        accept={SUPPORTED_EXTENSIONS.DOCX}
        hidden
        ref={docxInputRef}
        onChange={handleDocxImport}
      />
      <input
        type="file"
        accept={SUPPORTED_EXTENSIONS.PDF}
        hidden
        ref={pdfInputRef}
        onChange={handlePdfImport}
      />
      {/* --- End Hidden Inputs --- */}
    </Box>
  );
}

// Main App component with providers
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LanguageProvider>
      <FormProvider>
        <PaginationProvider>
           <LayoutProvider>
             <DesignProvider>
               <StyleProvider>
                  <BackgroundProvider>
                    <SpecialEffectProvider>
                      <BarStyleProvider>
                        <TypographyProvider>
                          <TemplateProvider>
                            <ExportProvider>
                              <Router>
                                <Routes>
                                  <Route path="/" element={<LandingPage />} />
                                  <Route path="/app" element={<AppContent />} />
                                  <Route path="/impressum" element={<ImpressumPage />} />
                                  <Route path="/nutzungsbedingungen" element={<AGBPage />} />
                                  <Route path="/datenschutz" element={<DatenschutzPage />} />
                                </Routes>
                              </Router>
                            </ExportProvider>
                          </TemplateProvider>
                        </TypographyProvider>
                      </BarStyleProvider>
                    </SpecialEffectProvider>
                  </BackgroundProvider>
               </StyleProvider>
             </DesignProvider>
           </LayoutProvider>
        </PaginationProvider>
      </FormProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
